import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import classNames from 'classnames';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { setCookie } from '../../utils/cookie-helpers';
import {
	cpScript,
	initMarketoMunchkinWithCP,
} from '../../utils/third-party-scripts';
import { PortableText } from '../portableText';
import * as styles from './marketo-form.module.css';
import { recaptchaSetup } from './recaptchaSetup';

const recaptchaConfig = {
	fields: {
		recaptchaFinger: 'reCAPTCHALastUserFingerprint',
	},
	actions: {
		formSubmit: 'form',
	},
	debug: {
		testBadFinger: false,
	},
};

export function MarketoForm({
	backgroundColor,
	className,
	columnLayout,
	enableChiliPiper,
	enableRecaptcha = false,
	eyebrowTextOverride,
	formCtaCopy,
	formWidth,
	growsurf = false,
	headerTextOverride,
	isPopupForm,
	ldConversionTracking,
	ldEventKey,
	loadFormParams = ['//info.helloalma.com', '894-SZP-051'],
	marketoForm,
	onRender = () => { },
	onSuccess = () => { },
	resourceLayout = false,
	subheaderTextOverride,
	successMessage = 'We have received your message and will answer shortly!',
	setResourceFormCookie,
	thankYouMessageOverride,
}) {
	const {
		_rawDisclaimer,
		analyticsOnStart = {},
		analyticsOnSubmit = {},
		eyebrowText,
		formId,
		headerText,
		subheaderText,
		thankYouMessage,
		_rawThankYouMessage,
	} = marketoForm;

	const [formUrl, mktoId] = loadFormParams;

	// state
	const [formStarted, setFormStarted] = useState();
	const [showSubmitting, setShowSubmitting] = useState(false);
	const [showThankYouMessage, setShowThankYouMessage] = useState(false);

	// hooks
	const ldClient = useLDClient();

	const formContainerEl = useRef();
	const mktoFormEl = useRef();
	const mktoFirstInputEl = useRef();

	const gaOnSubmitHandler = () => {
		if (!analyticsOnSubmit?.enableGA4) {
			console.log('GA4 is not enabled');
			return;
		}

		const { ga4Event = {} } = analyticsOnSubmit;

		console.log('Analytics on submit', analyticsOnSubmit);

		if (window?.gtag) {
			console.log('Window gtag is enabled');
			window.gtag('event', ga4Event.name, {
				send_to: 'G-R0Z6J0E1GE',
				formId: formId,
			});
		}
	};

	const isValidForGrowsurf = (useGrowSurf, formValues, grsfParam) => {
		const isProvider =
			formValues.isConsumer == 'no' || !!formValues.License__c;
		return useGrowSurf && isProvider && !!grsfParam;
	};

	const growSurfTrackingHandler = async ({
		Email: email,
		FirstName: firstName,
		LastName: lastName,
	}) => {
		if (process.env.NODE_ENV === `production` && window?.growsurf) {
			try {
				await window.growsurf.addParticipant({
					email,
					firstName,
					lastName,
				});
				console.log('GrowSurf - participant was added successfully');
			} catch (err) {
				console.error({
					gsErr: err,
				});
			}
		} else {
			process.env.NODE_ENV !== `production` &&
				console.error(
					'GrowSurf - addParticipant failed. GrowSurf will not work in development'
				);
			!window?.growsurf &&
				console.error(
					'GrowSurf - addParticipant failed. GrowSurf not present on window'
				);
		}
		setTimeout(() => {
			setShowSubmitting(false);
			setShowThankYouMessage(true);
		}, 1000);
	};

	// only works if the first input field is the initial field that is targeted by user
	const formStartCheck = () => {
		const { ga4Event = {} } = analyticsOnStart;

		setFormStarted(true);

		if (!formStarted) {
			if (window?.gtag) {
				window.gtag('event', ga4Event.name, {
					send_to: 'G-R0Z6J0E1GE',
					formId: formId,
				});
			}
		} else {
			mktoFirstInputEl.current.removeEventListener(
				'input',
				formStartCheck
			);
		}
	};

	useEffect(() => {
		const url = new URL(document.location);

		const hashAndQuery = url.hash.split('?');
		const params = url.searchParams;

		// setup params as variables
		const calcTimeSavings = params.get('calcTimeSavings');
		const calcCostSavings = params.get('calcCostSavings');
		const grsfParam = params.get('grsf');

		if (hashAndQuery.length > 1) {
			hashAndQuery.reverse();
			window.history.pushState(
				{},
				'',
				url.origin + url.pathname + '?' + hashAndQuery.join('')
			);
		}

		window.MktoForms2?.onFormRender(() => {
			formContainerEl.current?.classList?.add('ready');
			onRender();
		});

		window.MktoForms2?.whenReady(() => {
			function isPlatform(platform) {
				if (navigator.userAgentData?.platform) {
					return navigator.userAgentData.platform === platform;
				} else {
					return new RegExp(platform).test(navigator.userAgent);
				}
			}

			let forms2BaseURL = '//info.helloalma.com/js/forms2/';

			window.MktoForms2?.Modernizr.load({
				test: isPlatform('Android'),
				yep: [forms2BaseURL + 'polyfills/inputmask/jquery.mask.min.js'],
				nope: [forms2BaseURL + 'polyfills/inputmask/inputmask.min.js'],
			});

			const formRows =
				mktoFormEl.current?.querySelectorAll('.mktoFormRow');

			mktoFirstInputEl.current =
				formRows?.[0]?.children[0]?.querySelector('input');
			mktoFirstInputEl.current?.addEventListener('input', formStartCheck);
		});

		!mktoFormEl.current?.children.length &&
			window.MktoForms2.loadForm(formUrl, mktoId, formId, (form) => {
				const submitButton = document.querySelector('.mktoButton');
				var formEl = form.getFormElem()[0],
					submitButtonEl = formEl.querySelector(
						"button[type='submit']"
					);

				if (formCtaCopy) {
					submitButton.innerHTML = formCtaCopy;
				}

				let isExtendedValidatorComplete = false;
				const briteverifyURL =
					'https://bpi.briteverify.com/api/public/v1/fullverify';

				// note about onValidate - it runs in an infinite loop until you break it
				// worst architecture pattern I have ever seen, but thats Marketo for you
				form.onValidate(() => {
					console.log('Running validation');
					if (!isExtendedValidatorComplete && !enableRecaptcha) {
						console.log(
							'Validating email and phone without recaptcha'
						);

						form.submitable(false);
						const emailElem = form.getFormElem().find('#Email');
						const phoneElem = form.getFormElem().find('#Phone');
						const email = emailElem.val();
						const phone = phoneElem.val();

						const requestOptions = {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization:
									'ApiKey: 669dbe58-45c6-444c-aca8-e7341de33264',
							},
							body: JSON.stringify({
								email: email,
								phone: phone,
							}),
							redirect: 'follow',
						};

						fetch(briteverifyURL, requestOptions)
							.then((response) => response.json())
							.then((data) => {
								const isEmailInvalid =
									data?.email?.status == 'invalid';
								const isPhoneInvalid =
									data?.phone?.status == 'invalid';

								if (isEmailInvalid) {
									console.log('Email is invalid');
									setShowSubmitting(false);
									form.submitable(false);
									form.showErrorMessage(
										'Invalid email.',
										emailElem
									);
								} else if (isPhoneInvalid) {
									setShowSubmitting(false);
									console.log('Phone is invalid');
									form.submitable(false);
									form.showErrorMessage(
										'Invalid phone.',
										phoneElem
									);
								} else {
									console.log('All set!');
									isExtendedValidatorComplete = true;
									form.submitable(true);
									form.submit();
								}
							})
							.catch((error) => {
								console.error(
									'There was an error with the briteverify script: ',
									error
								);
							});
					} else if (
						!isExtendedValidatorComplete &&
						enableRecaptcha
					) {
						console.log(
							'Validating email and phone with recaptcha'
						);

						form.submitable(false);
						form.locked = false;
						const emailElem = form.getFormElem().find('#Email');
						const phoneElem = form.getFormElem().find('#Phone');
						const email = emailElem.val();
						const phone = phoneElem.val();

						const requestOptions = {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization:
									'ApiKey: 669dbe58-45c6-444c-aca8-e7341de33264',
							},
							body: JSON.stringify({
								email: email,
								phone: phone,
							}),
							redirect: 'follow',
						};

						fetch(briteverifyURL, requestOptions)
							.then((response) => response.json())
							.then((data) => {
								const isEmailInvalid =
									data?.email?.status == 'invalid';
								const isPhoneInvalid =
									data?.phone?.status == 'invalid';

								if (isEmailInvalid) {
									console.log('Email is invalid');
									setShowSubmitting(false);
									form.submitable(false);
									form.showErrorMessage(
										'Invalid email.',
										emailElem
									);
								} else if (isPhoneInvalid) {
									console.log('Phone is invalid');
									setShowSubmitting(false);
									form.submitable(false);
									form.showErrorMessage(
										'Invalid phone.',
										phoneElem
									);
								} else {
									console.log('All set with validation!');
									isExtendedValidatorComplete = true;
									form.submitable(true);

									grecaptcha.ready(function () {
										grecaptcha
											.execute(
												process.env
													.GATSBY_GOOGLE_RECAPTCHA_KEY,
												{
													action: recaptchaConfig
														.actions.formSubmit,
												}
											)
											.then(function (recaptchaFinger) {
												let mktoFields = {};

												if (
													form.locked == false &&
													form.submitable() == true
												) {
													console.log(
														'Primary recaptcha response resolved'
													);

													form.locked = true;
													if (
														!recaptchaConfig.debug
															.testBadFinger
													) {
														mktoFields[
															recaptchaConfig.fields.recaptchaFinger
														] = recaptchaFinger;
													} else {
														mktoFields[
															recaptchaConfig.fields.recaptchaFinger
														] =
															'known bad fingerprint ' +
															Math.random();
													}

													console.log(
														'All set with recaptcha, submitting!'
													);
													form.addHiddenFields(
														mktoFields
													);
													form.submittable(true);
													form.submit();
												} else {
													console.log(
														'secondary recaptcha response resolved'
													);
												}
											});
									});
								}
							})
							.catch((error) => {
								console.error(
									'There was an error with the briteverify script: ',
									error
								);
							});
					} else {
						console.log('No validation needed, submitting!');
						return;
					}
				});

				form.onSubmit(() => {
					console.log('Marketo form submitted');
					if (calcCostSavings && calcTimeSavings) {
						form.setValues({
							Calc_Cost_Savings__c: calcCostSavings,
							Calc_Time_Savings__c: calcTimeSavings,
						});
					}
					setShowSubmitting(true);
				});

				form.onSuccess((values) => {
					console.log('Marketo form was submitted successfully!');
					// reset submitting message
					setShowSubmitting(false);

					// show thank you message
					setShowThankYouMessage(true);

					// fire analytics events
					gaOnSubmitHandler();
					onSuccess();

					console.log(
						'LD Conversion Tracking Enabled: ',
						ldConversionTracking
					);
					console.log('LD event key: ', ldEventKey);
					// fire LaunchDarkly conversion event
					if (ldConversionTracking && ldEventKey) {
						console.log('Tracking LaunchDarkly conversion event');
						const ldContext = ldClient?.getContext();

						ldClient.track(ldEventKey, ldContext);
					}

					isValidForGrowsurf(growsurf, values, grsfParam) &&
						growSurfTrackingHandler(values);

					// track form submission through snowplow
					trackSelfDescribingEvent({
						event: {
							schema: 'iglu:com.snowplowanalytics.snowplow/submit_form/jsonschema/1-0-0',
							data: {
								formId: formId,
							},
						},
					});

					// set cookie to prevent form from showing again on specific resource hub pages
					if (setResourceFormCookie) {
						const expirationDate = new Date(
							new Date().getTime() + 30 * 24 * 60 * 60 * 1000
						);
						const domain = window.location.hostname;

						setCookie(
							'resource_hub_form_submitted',
							'true',
							expirationDate,
							domain
						);
					}

					return false;
				});
			});

		return () => {
			mktoFirstInputEl.current?.removeEventListener(
				'input',
				formStartCheck
			);
		};
	}, [enableChiliPiper, formId, formUrl, mktoId, formStarted]);

	const thankYouOrSuccessMessage = thankYouMessageOverride ? (
		<PortableText blocks={thankYouMessageOverride} />
	) : thankYouMessage ? (
		<PortableText blocks={_rawThankYouMessage} />
	) : (
		<div>
			<h3 className='body-display'>Thank you</h3>
			<p>{successMessage}</p>
		</div>
	);

	const displayFormHeader =
		(!showThankYouMessage &&
			(eyebrowTextOverride ||
				eyebrowText ||
				headerTextOverride ||
				headerText)) ||
		subheaderTextOverride ||
		subheaderText;

	return (
		<>
			<Helmet>
				{enableChiliPiper && <script>{cpScript}</script>}
				{enableChiliPiper && (
					<script>{initMarketoMunchkinWithCP}</script>
				)}
				{enableRecaptcha && (
					<script async defer>
						{recaptchaSetup()}
					</script>
				)}
				{growsurf && (
					<script
						grsf-campaign={
							process.env.GATSBY_STAGING === 'true' ||
								process.env.NODE_ENV === 'development'
								? 'bkbq8z'
								: '6ya9kw'
						}
						async
						src='https://app.growsurf.com/growsurf.js?v=2.0.0'
					></script>
				)}
			</Helmet>
			<div
				className={classNames(
					resourceLayout
						? styles.resourceFormContainer
						: styles.marketoFormContainer,
					!resourceLayout && backgroundColor && `b${backgroundColor}`,
					className
				)}
				id='form'
				ref={formContainerEl}
			>
				{displayFormHeader && (
					<div className={styles.headerContainer}>
						{(eyebrowTextOverride || eyebrowText) && (
							<p className='heading-xs'>
								{eyebrowTextOverride || eyebrowText}
							</p>
						)}
						{(headerTextOverride || headerText) && (
							<h2
								className={
									isPopupForm
										? styles.popupHeaderText
										: 'heading-l'
								}
							>
								{headerTextOverride || headerText}
							</h2>
						)}
						{(subheaderTextOverride || subheaderText) && (
							<p className={styles.descriptionText}>
								{subheaderTextOverride || subheaderText}
							</p>
						)}
					</div>
				)}
				{!showThankYouMessage && (
					<form
						className={classNames(
							formWidth && formWidth,
							columnLayout && columnLayout
						)}
						id={`mktoForm_${formId}`}
						ref={mktoFormEl}
					/>
				)}
				{showThankYouMessage && (
					<div className={styles.thankYouMessageContainer}>
							{thankYouOrSuccessMessage}
					</div>
				)}
				{showSubmitting && (
					<div>
						<p>
							Submitting. Please do not leave or refresh the page.
						</p>
					</div>
				)}
				{!showThankYouMessage && !_rawDisclaimer && (
					<p className={classNames(styles.disclaimer, 'body-s')}>
						By submitting this form, you are agreeing to Alma's{' '}
						<a
							href='https://secure.helloalma.com/pp/'
							target='_blank'
							rel='noreferrer'
						>
							privacy policy.
						</a>
					</p>
				)}
				{!showThankYouMessage && _rawDisclaimer && (
					<PortableText
						blocks={_rawDisclaimer}
						className={styles.disclaimer}
					/>
				)}
			</div>
		</>
	);
}
